import { Component, Injector, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Agent, CreditsSpent, CreditsSpentItem } from '@trendbuild/trend-gpt-maker-api';
import { AbstractComponent, AgentTrendGPTService, Channel, ConfirmationComponent, GPTMakerIntegration, Integration, IntegrationService, WalletService, WalletTypeEnum } from 'lib-trend-core';
import { finalize, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AgentTrendgptModalComponent } from './agent-trendgpt-modal/agent-trendgpt-modal.component';

interface AgentWithMetadata extends Agent {
  metadata?: {
    channels?: { _id: string, name?: string, type?: string }[]
  }
}

type creditsSpentRecord = { name: string; total: number };
type creditsSpentByDate = { date: string; credits: number };

@Component({
    selector: 'app-agent-trendgpt',
    templateUrl: './agent-trendgpt.component.html',
    styleUrl: './agent-trendgpt.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentTrendgptComponent extends AbstractComponent {
  public integration: Integration;
  public assistants: Agent[];
  public assistant: AgentWithMetadata;
  public channels: Channel[] = [];
  private readonly currentYear = new Date().getFullYear();

  
  public credits: CreditsSpent;
  public creditsSpent = signal<Array<creditsSpentRecord>>([]);

  public sumAmount = 0;
  public last7DaysData: Array<creditsSpentByDate> = [];

  /** Shown in the info strip; managing the AI balance lives in /wallet/AI now. */
  public aiBalance = 0;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private integrationService: IntegrationService,
    private walletService: WalletService,
    private agentTrendGPTService: AgentTrendGPTService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getIntegrationTrendGPTByCompany();
    this.loadAiBalance();
  }

  /**
   * The wallet UI moved to /credits (SPEC-2026-08-typed-wallets RF-33). This
   * screen keeps only a read-only strip: the per-assistant charts below carry
   * attribution the ledger does not model, but the balance itself belongs to the
   * Credits area.
   */
  private loadAiBalance(): void {
    // `currentCredits`, not the wallet's `balance`: on the AI wallet `balance` is
    // the gross total of credits ever loaded. What is left to spend is that minus
    // the GPT Maker consumption, and the server derives it in one place for this
    // strip and for the Wallets area alike.
    this.walletService.getKpis(WalletTypeEnum.AI)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (kpis) => this.aiBalance = kpis?.currentCredits ?? 0,
        error: () => this.aiBalance = 0,
      });
  }

  create() {
    const dialogRef = this.dialog.open(AgentTrendgptModalComponent, {
      width: '1150px',
    });

    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$),
    ).subscribe(async (payload) => {
      if (payload) {

        try {
          const created = await firstValueFrom(this.agentTrendGPTService.createAgent(payload));

          this.alertService.success(this.t('agents.trendgpt.assistantCreatedSuccess', { name: created.name }));
          await this.setupChannelsAssistant(
            created.id,
            payload.channels,
            created.name
          );

          this.router.navigate([`/agents/trendgpt/${created.id}/edit`], { state: { channels: payload?.channels ?? [] } });
        } catch (error) {
          this.alertService.error(error.error.message);
        }
      }
    });
  }

  edit(assistant: Agent) {
    this.router.navigate([`/agents/trendgpt/${assistant.id}/edit`]);
  }

  getIntegrationTrendGPTByCompany() {
    this.loadingContent = true;
    this.integrationService.getIntegrationTrendGPTByCompany()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.loadingContent = false; })
      )
      .subscribe({
        next: async (integration: Integration) => {
          this.integration = integration;
          const gptMakerIntegration = integration.metadata as GPTMakerIntegration;

          const basicAgents = gptMakerIntegration.agents ?? [];
          this.assistants = [];
          await this.getAgentsById(basicAgents);

          this.getAgentsBalance();
        },
        error: (err) => {
          this.alertService.error(err.error.message);
        }
      });
  }

  // setupChannelsAssistant(agentId: string, channels: Channel[], agentName: string) {
  //   const metadata = this.integration.metadata || {};
  //   let agents = metadata['agents'] || [];

  //   const index = agents.findIndex((a: any) => a.id === agentId);

  //   if (index >= 0) {
  //     agents[index] = { name: agentName, id: agentId, createdAt: new Date(), channels };
  //   } else {
  //     agents.push({ name: agentName, id: agentId, createdAt: new Date(), channels });
  //   }

  //   metadata['agents'] = agents;

  //   const updatedIntegration = { ...this.integration, metadata };

  //   return new Promise<void>((resolve, reject) => {
  //     this.integrationService.update(this.integration._id, updatedIntegration)
  //       .pipe(takeUntil(this.destroy$))
  //       .subscribe({
  //         next: () => resolve(),
  //         error: (err) => {
  //           this.alertService.error(err.error.message);
  //           reject(err);
  //         },
  //       });
  //   });
  // }

  setupChannelsAssistant(agentId: string, channels: Channel[], agentName: string) {
    if (!agentId || !agentName) {
      return Promise.reject(new Error(this.t('agents.trendgpt.invalidAgentError')));
    }

    const metadata = { ...(this.integration.metadata || {}) };
    const agents = [...((metadata['agents'] as Array<any>) || [])];

    const index = agents.findIndex((a) => a.id === agentId);

    if (index >= 0) {
      agents[index] = { ...agents[index], name: agentName, id: agentId, channels };
    } else {
      agents.push({ name: agentName, id: agentId, createdAt: new Date(), channels });
    }

    metadata['agents'] = agents;
    const updatedIntegration = { ...this.integration, metadata };

    return new Promise<void>((resolve, reject) => {
      this.integrationService.update(this.integration._id, updatedIntegration)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (saved) => {
            this.integration = saved;
            resolve();
          },
          error: (err) => {
            this.alertService.error(err?.error?.message ?? this.t('agents.trendgpt.saveChannelsError'));
            reject(err);
          },
        });
    });
  }


  private async getAgentsBalance() {
    for (const assistant of this.assistants) {
      this.credits = await firstValueFrom(this.agentTrendGPTService.getCreditsSpent(assistant.id, { year: this.currentYear })) as CreditsSpent;

      this.creditsSpent.update((current) => [
        ...current, { name: assistant.name, total: this.credits.total }
      ]);

      const sorted = [...this.creditsSpent()].sort((a, b) => b.total - a.total);
      this.creditsSpent.set(sorted);

      this.sumAmount += this.credits.total;

      this.processLast7DaysData(this.credits.data);
    }
  }

  private processLast7DaysData(data: CreditsSpentItem[]) {
    const creditsByDay: Record<string, number> = {};

    data.forEach(item => {
      if (item) {
        const dateKey = `${String(item.day)}/${String(item.month)}`;

        if (creditsByDay[dateKey]) {
          creditsByDay[dateKey] += item.credits;
        } else {
          creditsByDay[dateKey] = item.credits;
        }
      }
    });

    const sortedDates = Object.entries(creditsByDay)
      .map(([date, credits]) => ({ date, credits }))
      .sort((a, b) => {
        const [dayA, monthA] = a.date.split('/').map(Number);
        const [dayB, monthB] = b.date.split('/').map(Number);

        if (monthA !== monthB) return monthB - monthA;
        return dayB - dayA;
      });

    this.last7DaysData = sortedDates.slice(0, 7);

    while (this.last7DaysData.length < 7) {
      this.last7DaysData.push({ date: '--/--', credits: 0 });
    }
  }

  async activeOrInactiveAgent(agentId: string) {
    try {
      const currentAssistant = this.assistants.find(a => a.id === agentId);

      if (!currentAssistant) {
        this.alertService.error(this.t('agents.trendgpt.assistantNotFound'));
        return;
      }

      if (currentAssistant['status'] === 'ACTIVE') {
        await firstValueFrom(this.agentTrendGPTService.inactiveAgent(agentId));
        this.alertService.success(this.t('agents.trendgpt.assistantDeactivatedSuccess'));
      } else {
        await firstValueFrom(this.agentTrendGPTService.activeAgent(agentId));
        this.alertService.success(this.t('agents.trendgpt.assistantActivatedSuccess'));
      }

      this.getIntegrationTrendGPTByCompany();
    } catch (error) {
      this.alertService.error(error.error.message);
    }
  }

  private async getAgentsById(agents: Agent[]) {
    try {
      this.assistants = [];

      for (const basicAgent of agents) {
        try {
          const completeAgent = await firstValueFrom(this.agentTrendGPTService.getAgentById(basicAgent.id));

          const mergedAgent = {
            ...completeAgent,
            channels: basicAgent['channels'] || []
          };

          this.assistants.push(mergedAgent);
        } catch (error) {
        }
      }

    } catch (error) {
      this.alertService.error(this.t('agents.trendgpt.loadAssistantsError'));
    }
  }


  delete(assistant: Agent) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
      data: this.t('agents.trendgpt.confirmDeleteAssistant', { name: assistant.name })
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (Boolean(result) === true) {
        this.assistants = this.assistants.filter(a => a.id !== assistant.id);
        await firstValueFrom(this.agentTrendGPTService.deleteAgentById(assistant.id));
        await firstValueFrom(this.integrationService.deleteAgent(this.integration._id, assistant.id));
      }
    });
  }
}
